import React, { useState, useEffect } from 'react';
import Form from '../Form';
import Modal from '../Modal';
import { useAuth } from '../../helpers/AuthContext';
import './ProductBulkEditor.css';
import taxonomy from 'mayhem-taxonomy';

export default function ProductBulkShippingEditor({
    selectedProducts,
    allProducts,
    onClose,
    onSave,
    channel
}) {
    const { fetch } = useAuth();
    const [formValues, setFormValues] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);

    const selectedProductsData = allProducts.filter(product =>
        selectedProducts.includes(product.uuid)
    );

    const categories = channel?.isRed ? taxonomy.categories_red : taxonomy.categories;

    const fields = [
        {
            name: "mode",
            type: "select",
            label: "Shipping Pricing Mode",
            options: [
                { value: "", label: "No Change" },
                { value: "none", label: "No Shipping Fee" },
                { value: "flat", label: "Flat Rate" },
                { value: "calculated", label: "Calculated at Checkout" },
                { value: "pickup", label: "Local Pickup Only" }
            ],
            help: "Set the shipping pricing mode for selected products. Flat and Calculated with enable 'USPS Ground Advantage' for all products."
        },
        {
            name: "flatRate",
            type: "number",
            label: "Flat Shipping Rate ($)",
            placeholder: "e.g. 5.00",
            help: "Set a flat shipping rate for USPS Ground Advantage on products with 'Flat Rate' mode."
        },
        {
            name: "allowCombinedShipping",
            type: "select",
            label: "Allow Combined Shipping",
            options: [
                { value: "", label: "No Change" },
                { value: "true", label: "Yes" },
                { value: "false", label: "No" }
            ],
            help: "Allow combined shipping discounts when customers buy multiple items."
        },
        {
            name: "secondaryRate",
            type: "number",
            label: "Secondary Shipping Rate ($)",
            placeholder: "e.g. 2.50",
            help: "Set a secondary shipping rate for combined shipping discounts."
        },
        {
            name: "originZIPCode",
            type: "text",
            label: "Origin ZIP Code",
            placeholder: "e.g. 90210",
            help: "What ZIP code do you ship from?"
        },
        {
            name: "weight",
            type: "number",
            label: "Weight (lbs)",
            placeholder: "e.g. 1.5",
            help: "Set the weight for shipping calculations."
        },
        {
            name: "length",
            type: "number",
            label: "Length (inches)",
            placeholder: "e.g. 10",
            help: "Set the length for shipping calculations."
        },
        {
            name: "width",
            type: "number",
            label: "Width (inches)",
            placeholder: "e.g. 5",
            help: "Set the width for shipping calculations."
        },
        {
            name: "height",
            type: "number",
            label: "Height (inches)",
            placeholder: "e.g. 8",
            help: "Set the height for shipping calculations."
        }
    ];



    const handleSubmit = async () => {
        setIsSubmitting(true);
        try {
            // Filter out empty values (no change)
            const updates = Object.entries(formValues)
                .filter(([_, value]) => value !== '')
                .reduce((acc, [key, value]) => {
                    acc[key] = value;
                    return acc;
                }, {});

            if (Object.keys(updates).length === 0) {
                onClose();
                return;
            }

            const response = await fetch('/account/products/bulk-shipping-update', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    productIds: selectedProducts,
                    updates
                })
            });

            if (response.ok) {
                onSave(`Successfully updated ${selectedProducts.length} products`);
                onClose();
            } else {
                throw new Error('Failed to update products');
            }
        } catch (error) {
            console.error('Error updating products:', error);
            onSave('', 'Failed to update products. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const modalFooter = (
        <div className="bulk-editor-actions">
            <button
                type="button"
                className="button secondary"
                onClick={onClose}
                disabled={isSubmitting}
            >
                Cancel
            </button>
            <button
                type="submit"
                className="button primary"
                onClick={handleSubmit}
                disabled={isSubmitting}
            >
                {isSubmitting ? 'Updating...' : 'Update Products'}
            </button>
        </div>
    );

    return (
        <Modal
            isOpen={true}
            onClose={onClose}
            title={`Edit Shipping for ${selectedProducts.length} Products`}
            size="medium"
            footer={modalFooter}
            closeOnOverlayClick={!isSubmitting}
            ariaLabelledBy="bulk-editor-title"
            ariaDescribedBy="bulk-editor-content"
        >
            <div className="bulk-editor-content" id="bulk-editor-content">
                <div className="selected-products">
                    <h4>Selected Products:</h4>
                    <ul>
                        {selectedProductsData.map(product => (
                            <li key={product.uuid}>
                                {product.name} ({product.type})
                            </li>
                        ))}
                    </ul>
                </div>

                <Form
                    id="bulk-editor-form"
                    fields={fields}
                    values={formValues}
                    onChange={setFormValues}
                    onSubmit={handleSubmit}
                    className="bulk-editor-form"
                />
            </div>
        </Modal>
    );
}
